<template>
  <Popover ref="panel" class="w-[26rem] max-w-[calc(100vw-2rem)]" @show="handleShow">
    <div class="flex max-h-[70vh] w-full flex-col">
      <div class="px-4 pb-3 pt-3">
        <div class="text-sm font-semibold text-charcoal-900 dark:text-charcoal-100">Capabilities</div>
        <p class="mt-0.5 text-xs text-charcoal-500 dark:text-charcoal-400">Tools the assistant can use to help you.</p>
      </div>

      <div class="border-t border-charcoal-100 px-3 pb-2 pt-3 dark:border-charcoal-800">
        <div class="relative">
          <Icon icon="heroicons:magnifying-glass"
            class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-charcoal-400 dark:text-charcoal-500" />
          <input ref="searchInput" v-model="query" type="text" placeholder="Search tools..."
            class="w-full rounded-xl border border-charcoal-200 bg-charcoal-50/60 py-2 pl-9 pr-8 text-sm text-charcoal-800 outline-none transition-colors placeholder-charcoal-400 focus:border-iris-400 focus:bg-white focus:ring-2 focus:ring-iris-400/30 dark:border-charcoal-700 dark:bg-charcoal-850 dark:text-charcoal-100 dark:placeholder-charcoal-500 dark:focus:border-iris-500 dark:focus:bg-charcoal-900" />
          <button v-if="query" type="button" @click="query = ''"
            class="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-charcoal-400 transition-colors hover:bg-charcoal-200/60 hover:text-charcoal-600 dark:text-charcoal-500 dark:hover:bg-charcoal-700 dark:hover:text-charcoal-300"
            aria-label="Clear search">
            <Icon icon="heroicons:x-mark" class="size-3.5" />
          </button>
        </div>
      </div>

      <div class="agent-chat-scrollbar flex-1 overflow-y-auto px-2 pb-1 pt-1">
        <div v-if="loading" class="flex flex-col gap-1.5 px-2 py-1.5">
          <div v-for="i in 4" :key="i" class="h-12 animate-pulse rounded-xl bg-charcoal-100 dark:bg-charcoal-800" />
        </div>

        <div v-else-if="error" class="px-2 py-8 text-center text-sm text-peach-500">{{ error }}</div>

        <div v-else-if="!filteredTools.length"
          class="flex flex-col items-center gap-2 px-2 py-10 text-center text-charcoal-400 dark:text-charcoal-500">
          <Icon icon="heroicons:magnifying-glass" class="size-5" />
          <span class="text-sm italic">No tools match "{{ query }}".</span>
        </div>

        <ul v-else class="flex flex-col gap-0.5">
          <li v-for="toolItem in filteredTools" :key="toolItem.name">
            <button type="button"
              class="flex w-full cursor-pointer gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-charcoal-50 dark:hover:bg-charcoal-850"
              @click="selectTool(toolItem)">
              <div class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                :class="categoryFor(toolItem.name).classes">
                <Icon :icon="categoryFor(toolItem.name).icon" class="size-4" />
              </div>
              <div class="min-w-0">
                <!-- eslint-disable vue/no-v-html -->
                <div class="text-sm font-medium text-charcoal-800 dark:text-charcoal-200"
                  v-html="highlight(humanize(toolItem.name))" />
                <div class="text-xs leading-relaxed text-charcoal-500 dark:text-charcoal-400"
                  v-html="highlight(toolItem.description)" />
                <!-- eslint-enable vue/no-v-html -->
              </div>
            </button>
          </li>
        </ul>
      </div>

      <div v-if="!loading && !error && tools.length"
        class="border-t border-charcoal-100 px-4 py-2 text-[11px] text-charcoal-400 dark:border-charcoal-800 dark:text-charcoal-500">
        {{ filteredTools.length }} of {{ tools.length }} tools
      </div>
    </div>
  </Popover>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { Icon } from '@iconify/vue'
import Popover from '@/components/Core/Popover.vue'
import { AGENT_ACCENT_CLASSES } from '@/components/Agent/agentAccentClasses.js'
import useAssistantTools from '@/composables/useAssistantTools.js'

const emit = defineEmits(['select'])

const panel = ref(null)
const searchInput = ref(null)
const query = ref('')
const { tools, loading, error, ensureLoaded } = useAssistantTools()

// Clicking a capability runs the same run/template dispatch as picking its
// matching "/command" from the chat's slash-command popover (see
// AssistantChat.vue's handleCapabilitySelect and buildSlashCommands' `tool`
// field in useMentionAutocomplete.js) — just without ever typing the "/".
function selectTool(toolItem) {
  emit('select', toolItem)
  panel.value?.hide()
}

// "fieldlens" shows up as a single, unspaced token in a couple of tool names,
// unlike everywhere else in the product where it's written as two words.
const WORD_OVERRIDES = { fieldlens: 'Field Lens' }

function humanize(name) {
  return String(name ?? '')
    .split('_')
    .filter(Boolean)
    .map((word) => WORD_OVERRIDES[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// A rough "what kind of thing is this" read on the tool's name (find/search/create/...)
// just to give the list some visual scannability — not worth per-tool config for
// a flat list of ~13 tools that already follow a verb_noun naming convention.
const CATEGORIES = [
  { prefix: 'find', icon: 'heroicons:magnifying-glass-circle', classes: AGENT_ACCENT_CLASSES.sky },
  { prefix: 'search', icon: 'heroicons:magnifying-glass', classes: AGENT_ACCENT_CLASSES.iris },
  { prefix: 'create', icon: 'heroicons:plus-circle', classes: AGENT_ACCENT_CLASSES.mantis },
  { prefix: 'boost', icon: 'heroicons:rocket-launch', classes: AGENT_ACCENT_CLASSES.peach },
  { prefix: 'check', icon: 'heroicons:shield-check', classes: AGENT_ACCENT_CLASSES.amber },
  { prefix: 'get', icon: 'heroicons:chart-bar', classes: AGENT_ACCENT_CLASSES.amber },
  { prefix: 'summarize', icon: 'heroicons:document-text', classes: AGENT_ACCENT_CLASSES.amber },
]
const DEFAULT_CATEGORY = { icon: 'heroicons:wrench-screwdriver', classes: AGENT_ACCENT_CLASSES.charcoal }

function categoryFor(name) {
  const prefix = String(name ?? '').split('_')[0]
  return CATEGORIES.find((category) => category.prefix === prefix) ?? DEFAULT_CATEGORY
}

const filteredTools = computed(() => {
  const term = query.value.trim().toLowerCase()
  if (!term) return tools.value

  return tools.value.filter((toolItem) =>
    humanize(toolItem.name).toLowerCase().includes(term) || toolItem.description.toLowerCase().includes(term)
  )
})

function escapeHtml(text) {
  return text.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[char]))
}

// Wraps the matched substring in <mark> so search feels responsive, the way
// command palettes (Raycast, Linear, Spotlight) highlight matches as you type.
function highlight(text) {
  const safe = escapeHtml(String(text ?? ''))
  const term = query.value.trim()

  if (!term) return safe

  const pattern = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig')

  return safe.replace(pattern, '<mark class="rounded bg-iris-200/70 px-0.5 text-charcoal-900 dark:bg-iris-500/40 dark:text-white">$1</mark>')
}

// ensureLoaded is called directly (not via @show) so data-loading doesn't
// depend on the underlying Popover's event firing — a plain function call is
// harder to get wrong than relying on attribute-fallthrough event forwarding.
function toggle(event) {
  ensureLoaded()
  panel.value?.toggle(event)
}

// Fresh search box every time the popover opens, like a command palette.
function handleShow() {
  query.value = ''
  nextTick(() => searchInput.value?.focus())
}

defineExpose({ toggle })
</script>
